import React, { useEffect, useState } from 'react';
import { supabase } from './supabase';
import './OrderTracking.css';

// Pantalla PÚBLICA de seguimiento (sin login). Se monta desde index.tsx cuando la
// URL es /seguimiento/<token>. Sistema visual de Krilic (Poppins 400–800, rojo #9C1E1F, berenjena,
// tinta, crema; bordes casi rectos, sin sombras ni gradientes) — ver OrderTracking.css.

interface TrackData {
  order_number: string;
  first_name: string;
  delivery_type: 'envio' | 'retiro' | null;
  ship_method: 'flex' | 'correo' | 'otro';
  pieces: number;
  phase: string;
  step: number;
  complete: boolean;
  cancelled: boolean;
  cabify_tracking?: string | null;
  cabify_live?: boolean;
  cabify_active?: boolean;
  updated_at: string;
  created_at: string;
  // 02/11/2026: plazos estimados (API de entregas de la tienda) para bajar las consultas
  products?: { name: string; qty: number; kind: 'stock' | 'fabricar'; plazo: string; cut: boolean }[];
  estimate?: {
    start: string; started: boolean; dispatch_from: string; dispatch_to: string;
    deliver_from: string; deliver_to: string; late: boolean; packed_at: string | null;
  } | null;
}

// "2026-11-12" → "jueves 12 de noviembre"; rango del mismo mes → "entre el lunes 9 y el jueves 12 de noviembre"
const parts = (iso: string) => {
  const d = new Date(`${iso}T12:00:00`);
  return { wd: d.toLocaleDateString('es-AR', { weekday: 'long' }), day: d.getDate(), month: d.toLocaleDateString('es-AR', { month: 'long' }) };
};
function fmtRange(a: string, b: string) {
  const x = parts(a), y = parts(b);
  if (a === b) return `el ${y.wd} ${y.day} de ${y.month}`;
  return x.month === y.month
    ? `entre el ${x.wd} ${x.day} y el ${y.wd} ${y.day} de ${y.month}`
    : `entre el ${x.wd} ${x.day} de ${x.month} y el ${y.wd} ${y.day} de ${y.month}`;
}

type Step = { key: string; name: string; desc: string };

const CONFIRMADO: Step = {
  key: 'confirmado', name: 'Pedido confirmado',
  desc: '¡Ya es nuestro! 🙌 Tu pedido entró en la cola de producción del taller.',
};
const PREPARACION: Step = {
  key: 'preparacion', name: 'En preparación',
  desc: 'Manos a la obra ✂️ Cada pieza se corta y se arma a mano en nuestro taller de Buenos Aires. Es el paso más largo — lo bueno se hace esperar.',
};

function buildSteps(deliveryType: string | null, shipMethod: string, cabifyLive?: boolean, cabifyActive?: boolean): Step[] {
  if (deliveryType === 'retiro') {
    return [
      CONFIRMADO,
      PREPARACION,
      {
        key: 'listo_retiro', name: '¡Listo para retirar!',
        desc: 'Prolijo y bien protegido 📦 Te esperamos de martes a viernes de 10 a 17hs o sábados de 10 a 14hs, en Palpa 3088, Colegiales. ¡Tocá timbre Krilic!',
      },
    ];
  }
  const despachadoDesc =
    shipMethod === 'flex'
      ? 'Ya salió del taller 🚚 Te llega en las próximas 48hs hábiles, de 16 a 21hs. Estamos más ansiosos que vos de que te llegue.'
      : shipMethod === 'correo'
      ? 'Ya está viajando con el correo 📮 Apenas tengamos el número de tracking te lo pasamos por WhatsApp.'
      : 'Tu pedido ya salió del taller 🚚 Cualquier detalle de la entrega te lo pasamos por WhatsApp.';
  const steps: Step[] = [
    CONFIRMADO,
    PREPARACION,
    { key: 'empaquetado', name: 'Pedido empaquetado', desc: cabifyActive
      ? 'Prolijo, protegido y con olor a nuevo 📦 En breve lo retira el repartidor.'
      : 'Prolijo, protegido y con olor a nuevo 📦 Listo para salir del taller.' },
    // Con Cabify el "despachado" no existe: cuando el repartidor lo retira ya está en reparto.
    // "Despachado" solo existe con correo: logística propia / envío gratis salen con Cabify y pasan directo a "en reparto".
    ...(cabifyActive || shipMethod !== 'correo' ? [] : [{ key: 'despachado', name: 'Pedido despachado', desc: despachadoDesc }]),
  ];
  // Envío propio: el estado interno 'entregado' significa "salió en reparto", así que el
  // cliente ve este paso (no "Entregado"). En correo la entrega real la confirma el tracking.
  if (shipMethod !== 'correo') {
    steps.push({
      key: 'en_reparto', name: 'En reparto',
      desc: cabifyLive
        ? '¡Está en camino AHORA mismo! 🛵 Seguí al repartidor en vivo con el botón de arriba. Estamos más ansiosos que vos de que te llegue.'
        : 'Se entrega entre hoy y mañana, de 16 a 21hs. ¡Ya falta nada! Estamos más ansiosos que vos de que te llegue 🤞',
    });
  }
  return steps;
}

function doneCopy(deliveryType: string | null): Step {
  return deliveryType === 'retiro'
    ? { key: 'retirado', name: '¡Retirado!', desc: 'Gracias por pasar 🧡 ¡Que lo disfrutes!' }
    : { key: 'entregado', name: '¡Entregado!', desc: 'Llegó a destino ✨ ¡Que ilumine tu espacio!' };
}

function firstNamePretty(n: string) {
  if (!n) return '';
  const low = n.toLowerCase();
  if (low === 'showroom' || low === 'cliente') return '';
  return n.charAt(0).toUpperCase() + n.slice(1);
}

const Check = () => (
  <svg viewBox="0 0 24 24" fill="none"><path d="M5 12.5l4.5 4.5L19 7" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" /></svg>
);

export default function OrderTracking() {
  const [data, setData] = useState<TrackData | null>(null);
  const [state, setState] = useState<'loading' | 'ok' | 'notfound' | 'error'>('loading');

  useEffect(() => {
    const l = document.createElement('link');
    l.rel = 'stylesheet';
    l.href = 'https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700;800&display=swap';
    document.head.appendChild(l);
    document.title = 'Estado de tu pedido · Krilic';

    const token = window.location.pathname.replace(/^\/seguimiento\//, '').replace(/\/+$/, '').trim();
    if (!token) { setState('notfound'); return; }

    supabase.functions
      .invoke('track-order', { body: { token } })
      .then(({ data, error }) => {
        if (error || !data || (data as any).error) {
          setState((data as any)?.error === 'not_found' ? 'notfound' : 'error');
          return;
        }
        setData(data as TrackData);
        setState('ok');
      })
      .catch(() => setState('error'));
  }, []);

  return (
    <div className="kr-track">
      <div className="kr-stage">
        <div className="kr-sheet">
          <div className="kr-head">
            <img src="/logo.png" alt="Krilic" />
            <span className="kr-mono kr-tag">Deco que ilumina</span>
          </div>

          {state === 'loading' && (
            <div className="kr-state"><div className="kr-dot-load" /><p>Buscando tu pedido…</p></div>
          )}
          {state === 'notfound' && (
            <div className="kr-state">
              <h2 className="kr-display">No encontramos ese pedido</h2>
              <p>Revisá que el link esté completo. Si te quedan dudas, escribinos y lo vemos juntos.</p>
            </div>
          )}
          {state === 'error' && (
            <div className="kr-state">
              <h2 className="kr-display">Se nos apagó la luz un segundo</h2>
              <p>Probá recargar la página en un ratito.</p>
            </div>
          )}
          {state === 'ok' && data && <Body data={data} />}
        </div>
      </div>
    </div>
  );
}

function Body({ data }: { data: TrackData }) {
  const name = firstNamePretty(data.first_name);
  const steps = buildSteps(data.delivery_type, data.ship_method, data.cabify_live, data.cabify_active);
  // El paso activo se busca por fase (la cantidad de pasos varía: Cabify no tiene "despachado")
  let litIndex = data.complete ? steps.length : steps.findIndex((s) => s.key === data.phase);
  if (!data.complete && litIndex < 0) litIndex = Math.min(data.step, steps.length - 1);
  const hero = data.cancelled
    ? { name: 'Pedido cancelado', desc: 'Si creés que es un error, escribinos y lo revisamos enseguida.' }
    : data.complete
    ? doneCopy(data.delivery_type)
    : steps[litIndex];
  const heroTitle = hero.name.endsWith('!') ? hero.name : `${hero.name}.`;
  const est = data.estimate;
  const retiro = data.delivery_type === 'retiro';
  const inPrep = !data.cancelled && !data.complete && data.phase === 'preparacion';
  const num = data.order_number;
  const numPretty = num.startsWith('K-') || num.startsWith('BT-') ? num : `#${num}`;

  return (
    <>
      <div className="kr-greet">
        <h1 className="kr-display kr-hi">{name ? `¡Hola, ${name}!` : '¡Hola!'}</h1>
        <div className="kr-mono kr-ord">
          Pedido <b>{numPretty}</b>
          {data.pieces > 0 && <span> · {data.pieces} {data.pieces === 1 ? 'pieza' : 'piezas'}</span>}
        </div>
      </div>

      <div className={`kr-hero ${data.cancelled ? 'kr-hero-cancel' : ''} ${data.complete ? 'kr-hero-done' : ''}`}>
        <div className="kr-hero-body">
          <div className="kr-mono kr-eyebrow"><span className="kr-eb-dot" />{data.cancelled ? 'Estado' : data.complete ? 'Pedido completado' : 'Estado actual'}</div>
          <h2 className="kr-display kr-hero-title">{heroTitle}</h2>
          <p className="kr-hero-desc">{hero.desc}</p>
          {inPrep && est?.late && (
            <p className="kr-hero-eta"><b>Viene un poco más lento de lo previsto.</b> Ya estamos encima y te avisamos por WhatsApp apenas esté listo.</p>
          )}
          {data.cabify_live && data.cabify_tracking && !data.complete && !data.cancelled && (
            <a
              href={data.cabify_tracking}
              target="_blank"
              rel="noopener noreferrer"
              className="kr-live-btn"
            >
              🛵 Seguir al repartidor en vivo
            </a>
          )}
        </div>
      </div>

      {!data.cancelled && !data.complete && est && (
        <section className="kr-block">
          <div className="kr-mono kr-block-title">Fechas estimadas</div>
          {!est.started && (
            <p className="kr-note">⏳ El plazo empieza a contar cuando se acredita el pago. Las fechas de abajo son si se acredita hoy.</p>
          )}
          <div className="kr-eta-grid">
            <div className="kr-eta">
              <div className="kr-eta-label">{retiro ? 'Listo para retirar' : 'Sale del taller'}</div>
              <div className="kr-eta-val">{fmtRange(est.dispatch_from, est.dispatch_to)}<span className="kr-ast">*</span></div>
            </div>
            {!retiro && (
              <div className="kr-eta">
                <div className="kr-eta-label">{data.ship_method === 'correo' ? 'Llega con el correo' : 'Te llega'}</div>
                <div className="kr-eta-val">{fmtRange(est.deliver_from, est.deliver_to)}<span className="kr-ast">*</span></div>
              </div>
            )}
          </div>
          <p className="kr-foot-note">
            <span className="kr-ast">*</span> Son fechas <b>estimadas</b>: salen del plazo de producción de cada producto, contado desde que se confirmó el pago.
            Los días corridos incluyen fines de semana; los hábiles no cuentan sábados, domingos ni feriados.
            Si algo cambia, te avisamos por WhatsApp.
          </p>
        </section>
      )}

      {!data.cancelled && (
        <div className="kr-timeline">
          {steps.map((s, i) => {
            const cls = i < litIndex ? 'done' : i === litIndex ? 'active' : 'pending';
            return (
              <div key={s.key} className={`kr-step kr-${cls}`}>
                <div className="kr-marker">
                  {cls === 'done' ? <Check /> : <span />}
                </div>
                <div className="kr-step-body">
                  <div className="kr-mono kr-num">
                    {String(i + 1).padStart(2, '0')}{cls === 'active' ? ' · Ahora' : ''}
                  </div>
                  <div className="kr-name">{s.name}</div>
                  <div className="kr-desc">{s.desc}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {!data.cancelled && !!data.products?.length && (
        <section className="kr-block">
          <div className="kr-mono kr-block-title">Lo que pediste</div>
          <ul className="kr-items">
            {data.products.map((p, i) => (
              <li key={i} className="kr-item">
                <div className="kr-item-name">{p.qty > 1 ? `${p.qty} × ` : ''}{p.name}</div>
                <div className="kr-item-meta">
                  {p.kind === 'stock'
                    ? <span className="kr-pill kr-pill-stock">En stock · sale en {p.plazo}</span>
                    : <span className="kr-pill">Se fabrica a pedido · {p.plazo}</span>}
                </div>
              </li>
            ))}
          </ul>
          {data.products.length > 1 && (
            <p className="kr-foot-note">Si pediste varias cosas, sale todo junto cuando está listo lo que más tarda, así te llega en un solo envío.</p>
          )}
        </section>
      )}

      {!data.cancelled && !data.complete && (
        <section className="kr-block">
          <div className="kr-mono kr-block-title">Preguntas frecuentes</div>
          <div className="kr-faq">
            <details>
              <summary>¿Por qué tarda unos días?</summary>
              <p>Casi todo lo que vendemos se fabrica a pedido: cortamos el acrílico, lo pulimos y lo armamos a mano para vos. Por eso el plazo de producción es el paso más largo.</p>
            </details>
            <details>
              <summary>¿Me van a avisar?</summary>
              <p>Sí. Te escribimos por WhatsApp {retiro ? 'cuando esté listo para retirar' : 'cuando sale del taller'}{data.ship_method === 'correo' ? ', con el número de seguimiento del correo' : ''}. Esta página también se actualiza sola en cada paso.</p>
            </details>
            {retiro ? (
              <details>
                <summary>¿Dónde y cuándo lo retiro?</summary>
                <p>En nuestro showroom de Palpa 3088, Colegiales: martes a viernes de 10 a 17 hs y sábados de 10 a 14 hs. Esperá el aviso de "listo para retirar" antes de venir.</p>
              </details>
            ) : data.ship_method === 'correo' ? (
              <details>
                <summary>¿Cuánto tarda el correo?</summary>
                <p>Una vez despachado, el correo suele tardar entre 2 y 5 días hábiles según la zona. Con el número de seguimiento lo podés ver en la web del correo.</p>
              </details>
            ) : (
              <details>
                <summary>¿Cómo es la entrega?</summary>
                <p>Lo lleva un repartidor nuestro (Cabify). El día que sale te avisamos por WhatsApp y te pasamos el link para seguirlo en vivo. Suele entregarse entre las 16 y las 21 hs. Si no hay nadie, el repartidor vuelve a intentar.</p>
              </details>
            )}
            <details>
              <summary>¿Puedo cambiar algo del pedido?</summary>
              <p>Si todavía está en preparación, escribinos por WhatsApp lo antes posible y vemos si se puede.</p>
            </details>
          </div>
        </section>
      )}

      <div className="kr-foot">
        <p className="kr-display kr-made">Hecho a mano en Buenos Aires<span className="kr-heart">.</span></p>
        <p className="kr-help">¿Algo raro con tu pedido? Escribinos por WhatsApp y lo vemos juntos.</p>
      </div>
    </>
  );
}
